import type {ReactNode} from "react";
import {
    CheckCircleIcon,
    ChevronRightIcon,
    ClockIcon,
} from "@heroicons/react/24/outline";
import {toDecimalFromMoney, type Money} from "../../../../../lib/Money.ts";
import {cn} from "../../../../../lib/utils.ts";
import {MoneyLabel} from "../../../../general/MoneyLabel.tsx";
import type {DocumentSettlement} from "../../types/types.ts";

export const DRILL_DOWN_MIN_WIDTH = "min-w-[54rem]";

export function DrillDownColgroup() {
    return (
        <colgroup>
            <col style={{width: "3.5rem"}} />
            <col style={{width: "2rem"}} />
            <col style={{width: "8rem"}} />
            <col />
            <col style={{width: "6rem"}} />
            <col style={{width: "8.5rem"}} />
            <col style={{width: "9rem"}} />
            <col style={{width: "5.5rem"}} />
        </colgroup>
    );
}

function settlementLabel(settlement: DocumentSettlement): string {
    const parts = [settlement.relation_name, settlement.invoice_number].filter(
        (part): part is string => !!part && part.trim().length > 0,
    );
    return parts.length ? parts.join(" · ") : "Onbekende factuur";
}

interface BookingGroupBoxProps {
    settlement?: DocumentSettlement;
    label?: string;
    net: Money;
    showStatus: boolean;
    expanded: boolean;
    onToggle: () => void;
    children: ReactNode;
}

export function BookingGroupBox({
    settlement,
    label,
    net,
    showStatus,
    expanded,
    onToggle,
    children,
}: BookingGroupBoxProps) {
    return (
        <div className={cn("flex flex-col gap-1.5", DRILL_DOWN_MIN_WIDTH)}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className={cn(
                    "flex items-center gap-2 px-1 text-left",
                    !expanded && "border-b border-gray-200 pb-2",
                )}
            >
                <ChevronRightIcon
                    className={cn(
                        "size-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
                        expanded && "rotate-90",
                    )}
                    aria-hidden
                />
                <span className="text-sm font-semibold text-foreground">
                    {settlement ? settlementLabel(settlement) : label}
                </span>
                {showStatus &&
                    (settlement ? <SettlementStatus settlement={settlement} /> : <NetStatus net={net} />)}
            </button>
            {expanded && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full table-fixed">
                        <DrillDownColgroup />
                        <tbody className="[&>tr:last-child]:border-b-0">{children}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatusLabel({children}: {children: ReactNode}) {
    return (
        <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-sm font-normal tabular-nums text-muted-foreground">
            {children}
        </span>
    );
}

function NetStatus({net}: {net: Money}) {
    if (net.signed_amount === 0) {
        return (
            <StatusLabel>
                <CheckCircleIcon className="size-4 shrink-0" aria-hidden />
                Afgewikkeld
            </StatusLabel>
        );
    }

    return (
        <StatusLabel>
            <ClockIcon className="size-4 shrink-0" aria-hidden />
            <MoneyLabel
                value={Math.abs(toDecimalFromMoney(net))}
                currency={net.currency}
                showCurrency={true}
            />
            <span>open</span>
        </StatusLabel>
    );
}

export function SettlementStatus({settlement}: {settlement: DocumentSettlement}) {
    const open = Math.abs(toDecimalFromMoney(settlement.open_amount));
    const total = Math.abs(toDecimalFromMoney(settlement.total_amount));
    const isSettled = settlement.open_amount.signed_amount === 0;
    const isFullyUnpaid = !isSettled && open === total;

    if (isSettled) {
        return (
            <StatusLabel>
                <CheckCircleIcon className="size-4 shrink-0" aria-hidden />
                Betaald
            </StatusLabel>
        );
    }

    if (isFullyUnpaid) {
        return (
            <StatusLabel>
                <ClockIcon className="size-4 shrink-0" aria-hidden />
                Onbetaald
            </StatusLabel>
        );
    }

    return (
        <StatusLabel>
            <ClockIcon className="size-4 shrink-0" aria-hidden />
            <MoneyLabel value={open} currency={settlement.open_amount.currency} showCurrency={true} />
            <span>open van</span>
            <MoneyLabel value={total} currency={settlement.total_amount.currency} showCurrency={true} />
        </StatusLabel>
    );
}
